import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { _ } from '/@/locale';
import { reverse } from '/@/utils/router';
import { useCropMonitoringById } from './crop-monitoring/use-crop-monitoring';
import { useFieldById } from './fields';
import { useRecommendationById } from './my-recommendations/useFilterMyRecommendations';
import { useGetUserNode, useGetUserNodes, useOwnUserNode } from './nodes';
import { useIsValidatedAdvisor, useIsValidatedConsultant, useIsValidatedRV, useIsValidatedSquad } from './session';
import { useVisitReportById } from './visit-reports';

export type Breadcrumb = {
    label: string;
    onClick?: () => void;
    href?: string;
};

export const useBreadcrumbs = (viewName: string): Breadcrumb[] => {
    const navigate = useNavigate();
    const params = useParams();

    const isRv = useIsValidatedRV();
    const isConsultant = useIsValidatedConsultant();
    const isSquadCoach = useIsValidatedSquad();
    const isAdvisor = useIsValidatedAdvisor();

    // Pre-call potentially used hooks (hooks cannot be called conditionally)
    const visitReportId = Number(params.visitReportId);
    const visitReportQuery = useVisitReportById(visitReportId, { enabled: !!visitReportId });
    const visitReport = visitReportQuery?.data;
    const recommendationQuery = useRecommendationById(Number(params.recommendationId));
    const fieldId = Number(params.fieldId);
    const cropMonitoringId = Number(params.cropMonitoringId);
    const cropMonitoringQuery = useCropMonitoringById(cropMonitoringId);
    const fieldQuery = useFieldById(
        { id: fieldId || recommendationQuery.data?.field || cropMonitoringQuery.data?.field },
        { enabled: !!fieldId || !!recommendationQuery.data?.field || !!cropMonitoringQuery.data?.field }
    );
    const growerId =
        visitReport?.userNode || recommendationQuery.data?.userNode || params.growerNodeId || fieldQuery.data?.userNode;
    const growerQuery = useGetUserNode(Number(growerId));
    const growerParentIds = growerQuery.data?.parents;
    const parentsNodeQuery = useGetUserNodes(growerParentIds || []);
    const parentNode = parentsNodeQuery.data.find((node) =>
        isAdvisor ? node?.type === 'advisor' : node?.type === 'rv'
    );
    const ownUserNodeQuery = useOwnUserNode();
    const isOwnUser = ownUserNodeQuery.data?.id === growerId;

    const [searchParams] = useSearchParams();
    const tab = searchParams.get('tab') || 'current-campaign';
    const rvId = Number(params.rvId);
    const rvQuery = useGetUserNode(rvId as number);

    const cropProtectionRecommendationLabel = recommendationQuery.data?.weedV2
        ? _('Recomendación de herbicidas')
        : recommendationQuery.data?.diseaseRecommendation
          ? _('Recomendación de fungicidas')
          : recommendationQuery.data?.plagueRecommendation
            ? _('Recomendación de insecticidas')
            : recommendationQuery.data?.seedTreatmentRecommendation
              ? _('Recomendación de tratamiento de semillas')
              : '';

    return useMemo(() => {
        const crumbs: Breadcrumb[] = [];
        const baseCrumbs: Breadcrumb[] = [];

        if (isSquadCoach) {
            baseCrumbs.push({
                label: parentNode?.name || '',
                onClick: () => navigate(reverse('RVs.detail', { params: { rvId: parentNode?.id }, qs: { tab } })),
            });
        }

        if (!isOwnUser) {
            if (isConsultant) {
                baseCrumbs.push({ label: _('RVs'), onClick: () => navigate(reverse('RVs.list', { qs: { tab } })) });
                baseCrumbs.push({
                    label: parentNode?.name || '',
                    onClick: () => navigate(reverse('RVs.detail', { params: { rvId: parentNode?.id }, qs: { tab } })),
                });
            }
            if (isRv || isAdvisor) {
                baseCrumbs.push({
                    label: _('Productores'),
                    onClick: () => navigate(reverse('Growers.list', { qs: { tab } })),
                });
            }
            if (growerQuery?.data?.id) {
                baseCrumbs.push({
                    label: growerQuery?.data?.name || '',
                    onClick: () =>
                        navigate(reverse('Growers.detail', { params: { growerNodeId: growerQuery?.data?.id } })),
                });
            }
        } else {
            baseCrumbs.push({
                label: _('Mis lotes'),
                onClick: () => navigate(reverse('MyFields.list')),
            });
        }

        switch (viewName) {
            case 'visitReport.VisitReportDetail': {
                crumbs.push(...baseCrumbs);
                crumbs.push({ label: _('Informe de visita') });
                return crumbs;
            }
            case 'fieldMonitoring.FieldMonitoringDetail':
                crumbs.push(...baseCrumbs);

                if (parentsNodeQuery?.isValidating) {
                    crumbs.push({ label: '...' });
                    return crumbs;
                }

                crumbs.push({ label: fieldQuery?.data?.name as string });

                return crumbs;
            case 'recommendation.RecommendationDetail':
                crumbs.push(...baseCrumbs);

                if (parentsNodeQuery?.isValidating) {
                    crumbs.push({ label: '...' });
                    return crumbs;
                }

                crumbs.push({
                    label: fieldQuery?.data?.name as string,
                    onClick: () =>
                        navigate(reverse('fields.FieldDetail', { params: { fieldId: fieldQuery.data?.id } })),
                });
                crumbs.push({ label: _('Recomendación de híbrido') });

                return crumbs;

            case 'recommendation.densityRecommendationDetail':
                crumbs.push(...baseCrumbs);
                if (parentsNodeQuery?.isValidating) {
                    crumbs.push({ label: '...' });
                    return crumbs;
                }
                crumbs.push({
                    label: fieldQuery?.data?.name as string,
                    onClick: () =>
                        navigate(reverse('fields.FieldDetail', { params: { fieldId: fieldQuery.data?.id } })),
                });
                crumbs.push({ label: _('Recomendación de manejo') });
                return crumbs;

            case 'rvs.RVDetail':
                if (isConsultant) {
                    crumbs.push({
                        label: _('RVs'),
                        onClick: () => navigate(reverse('RVs.list', { qs: { tab } })),
                    });
                    crumbs.push({
                        label: rvQuery.data?.name || '',
                    });
                }
                return crumbs;

            case 'grower.GrowerDetail':
                crumbs.push(...baseCrumbs);
                return crumbs;

            case 'cropProtectionRecommendation.CropProtectionRecommendationDetail':
                crumbs.push(...baseCrumbs);
                if (parentsNodeQuery?.isValidating) {
                    crumbs.push({ label: '...' });
                    return crumbs;
                }
                crumbs.push({
                    label: fieldQuery.data?.name || '',
                    onClick: () =>
                        navigate(reverse('fields.FieldDetail', { params: { fieldId: fieldQuery.data?.id } })),
                });
                crumbs.push({
                    label: cropProtectionRecommendationLabel,
                });
                return crumbs;

            case 'fieldMonitoring.ReportDetail':
                crumbs.push(...baseCrumbs);
                if (parentsNodeQuery?.isValidating) {
                    crumbs.push({ label: '...' });
                    return crumbs;
                }
                crumbs.push({
                    label: fieldQuery.data?.name || '',
                    onClick: () =>
                        navigate(reverse('fields.FieldDetail', { params: { fieldId: fieldQuery.data?.id } })),
                });
                crumbs.push({
                    label: _('Reporte de campaña'),
                });
                return crumbs;

            default:
                return [];
        }
    }, [
        viewName,
        isRv,
        isConsultant,
        isAdvisor,
        isSquadCoach,
        growerQuery,
        navigate,
        rvQuery,
        parentsNodeQuery,
        tab,
        cropProtectionRecommendationLabel,
        fieldQuery,
        isOwnUser,
        parentNode,
    ]);
};

export default useBreadcrumbs;
